<script setup>
import { computed, onMounted, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import integrationAPI from 'dashboard/api/integrations';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

/**
 * @typedef {Object} KlaviyoProfileCounts
 * @property {number} profiles
 * @property {number} matched
 * @property {number} ambiguous
 * @property {number} unmatched
 * @property {number} field_conflicts Number of profiles with field conflicts.
 * @property {number} pending_outbound Number of profiles with pending outbound changes.
 * @property {number} raw_events Number of raw provider events ingested.
 * @property {number} parsed_events Number of clean parsed events promoted.
 * @property {Record<string, number>} parser_statuses Raw event parser status counts.
 */

/**
 * @typedef {Object} KlaviyoProfileSyncCursor
 * @property {string} state
 * @property {string|null} last_synced_at
 * @property {boolean} has_cursor
 */

/**
 * @typedef {Object} KlaviyoStatus
 * @property {boolean} connected
 * @property {string} status
 * @property {KlaviyoSubscriptionSmokeTest|null} subscription_smoke_test
 * @property {KlaviyoTwoWaySyncSettings} two_way_sync
 * @property {KlaviyoProfileCounts} counts
 * @property {KlaviyoProfileSyncCursor|null} cursor
 * @property {KlaviyoProfileSyncCursor|null} event_cursor
 */

/**
 * @typedef {Object} KlaviyoTwoWaySyncSettings
 * @property {boolean} enabled
 * @property {boolean} identity_sync_enabled
 * @property {boolean} dry_run
 * @property {Record<string, string>} conflict_strategy
 */

/**
 * @typedef {Object} KlaviyoSubscriptionSmokeTestChannel
 * @property {'accepted'|'failed'} status
 * @property {string} message_key
 * @property {string|null} provider_error
 */

/**
 * @typedef {Object} KlaviyoSubscriptionSmokeTest
 * @property {'success'|'warning'} status
 * @property {string} tested_at
 * @property {Record<string, string>} identifiers
 * @property {Record<string, KlaviyoSubscriptionSmokeTestChannel>} channels
 */

/**
 * Settings page for configuring and monitoring the Klaviyo CRM integration.
 */
const store = useStore();
const router = useRouter();
const { t, locale } = useI18n();

/**
 * Builds default two-way sync settings before a backend payload exists.
 *
 * @returns {KlaviyoTwoWaySyncSettings}
 */
function defaultTwoWaySyncSettings() {
  return {
    enabled: true,
    identity_sync_enabled: true,
    dry_run: true,
    conflict_strategy: {
      name: 'glovely_wins',
      phone: 'glovely_wins',
      company: 'glovely_wins',
      location: 'glovely_wins',
    },
  };
}

const isLoading = ref(true);
const isSaving = ref(false);
const isSavingTwoWaySettings = ref(false);
const isTesting = ref(false);
const isBackfilling = ref(false);
const isSyncingEvents = ref(false);
const isReplayingEvents = ref(false);
const isDisconnecting = ref(false);
const behaviorDialogRef = ref(null);
const disconnectDialogRef = ref(null);
const apiKey = ref('');
const apiRevision = ref('2026-04-15');
const eventReplaySelectors = ref({
  parserKey: '',
  providerMetricName: '',
  payloadShapeHash: '',
});
/** @type {import('vue').Ref<KlaviyoStatus|null>} */
const klaviyoStatus = ref(null);
/** @type {import('vue').Ref<KlaviyoTwoWaySyncSettings>} */
const twoWaySync = ref(defaultTwoWaySyncSettings());
const errorMessage = ref('');

const klaviyoIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'klaviyo'
);

const klaviyoHook = computed(() => unref(klaviyoIntegration)?.hooks?.[0]);
const isConnected = computed(() => klaviyoStatus.value?.connected);
/** @type {import('vue').ComputedRef<KlaviyoSubscriptionSmokeTest|null|undefined>} */
const subscriptionSmokeTest = computed(
  () => klaviyoStatus.value?.subscription_smoke_test
);
/** @type {import('vue').ComputedRef<Partial<KlaviyoProfileCounts>>} */
const profileCounts = computed(() => klaviyoStatus.value?.counts || {});
/** @type {import('vue').ComputedRef<KlaviyoProfileSyncCursor|null|undefined>} */
const profileSyncCursor = computed(() => klaviyoStatus.value?.cursor);
/** @type {import('vue').ComputedRef<KlaviyoProfileSyncCursor|null|undefined>} */
const eventSyncCursor = computed(() => klaviyoStatus.value?.event_cursor);
const fieldGroupLabels = computed(() => ({
  name: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.FIELD_GROUPS.NAME'),
  phone: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.FIELD_GROUPS.PHONE'),
  company: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.FIELD_GROUPS.COMPANY'),
  location: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.FIELD_GROUPS.LOCATION'),
}));
const conflictStrategyOptions = computed(() => [
  {
    value: 'manual_review',
    label: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.STRATEGIES.MANUAL_REVIEW'),
  },
  {
    value: 'klaviyo_wins',
    label: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.STRATEGIES.KLAVIYO_WINS'),
  },
  {
    value: 'glovely_wins',
    label: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.STRATEGIES.GLOVELY_WINS'),
  },
  {
    value: 'disabled',
    label: t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.STRATEGIES.DISABLED'),
  },
]);
const fieldStrategyControls = computed(() =>
  ['name', 'phone', 'company', 'location'].map(fieldGroup => ({
    key: fieldGroup,
    label: fieldGroupLabels.value[fieldGroup],
  }))
);
const behaviorSections = computed(() => [
  {
    key: 'overview',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.OVERVIEW.TITLE'
    ),
    bullets: [
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.OVERVIEW.BULLET_1'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.OVERVIEW.BULLET_2'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.OVERVIEW.BULLET_3'
      ),
    ],
  },
  {
    key: 'identity',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.IDENTITY.TITLE'
    ),
    bullets: [
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.IDENTITY.BULLET_1'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.IDENTITY.BULLET_2'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.IDENTITY.BULLET_3'
      ),
    ],
  },
  {
    key: 'fields',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.FIELDS.TITLE'
    ),
    bullets: [
      t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.FIELDS.BULLET_1'),
      t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.FIELDS.BULLET_2'),
      t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.FIELDS.BULLET_3'),
    ],
  },
  {
    key: 'conflicts',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONFLICTS.TITLE'
    ),
    bullets: [
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONFLICTS.BULLET_1'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONFLICTS.BULLET_2'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONFLICTS.BULLET_3'
      ),
    ],
  },
  {
    key: 'deletions',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.DELETIONS.TITLE'
    ),
    bullets: [
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.DELETIONS.BULLET_1'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.DELETIONS.BULLET_2'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.DELETIONS.BULLET_3'
      ),
    ],
  },
  {
    key: 'rateLimits',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.RATE_LIMITS.TITLE'
    ),
    bullets: [
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.RATE_LIMITS.BULLET_1'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.RATE_LIMITS.BULLET_2'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.RATE_LIMITS.BULLET_3'
      ),
    ],
  },
  {
    key: 'consent',
    title: t(
      'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONSENT.TITLE'
    ),
    bullets: [
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONSENT.BULLET_1'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONSENT.BULLET_2'
      ),
      t(
        'INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.SECTIONS.CONSENT.BULLET_3'
      ),
    ],
  },
]);
const smokeTestChannelLabels = computed(() => ({
  email: t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.CHANNELS.EMAIL'),
  sms: t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.CHANNELS.SMS'),
}));
const smokeTestMessageLabels = computed(() => ({
  email_accepted: t(
    'INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.MESSAGES.EMAIL_ACCEPTED'
  ),
  sms_accepted: t(
    'INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.MESSAGES.SMS_ACCEPTED'
  ),
  email_failed: t(
    'INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.MESSAGES.EMAIL_FAILED'
  ),
  sms_failed: t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.MESSAGES.SMS_FAILED'),
  sms_region_not_ready: t(
    'INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.MESSAGES.SMS_REGION_NOT_READY'
  ),
  unknown: t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.MESSAGES.UNKNOWN'),
}));
const smokeTestRows = computed(() =>
  ['email', 'sms'].map(channel => {
    const result = subscriptionSmokeTest.value?.channels?.[channel];
    return {
      channel,
      label: smokeTestChannelLabels.value[channel],
      identifier:
        subscriptionSmokeTest.value?.identifiers?.[
          channel === 'email' ? 'email' : 'phone_number'
        ],
      status: result?.status || 'unknown',
      message:
        smokeTestMessageLabels.value[result?.message_key || 'unknown'] ||
        smokeTestMessageLabels.value.unknown,
    };
  })
);
const smokeTestPanelClasses = computed(() =>
  subscriptionSmokeTest.value?.status === 'success'
    ? 'border-n-teal-4 bg-n-teal-3 text-n-teal-11'
    : 'border-n-amber-4 bg-n-amber-3 text-n-amber-11'
);
const parserStatusLabels = computed(() => ({
  unparsed: t(
    'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PARSER_STATUSES.UNPARSED'
  ),
  unsupported: t(
    'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PARSER_STATUSES.UNSUPPORTED'
  ),
  parsed: t(
    'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PARSER_STATUSES.PARSED'
  ),
  failed: t(
    'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PARSER_STATUSES.FAILED'
  ),
  replay_required: t(
    'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PARSER_STATUSES.REPLAY_REQUIRED'
  ),
}));
const parserStatusRows = computed(() =>
  ['unparsed', 'unsupported', 'parsed', 'failed', 'replay_required'].map(
    status => ({
      status,
      label: parserStatusLabels.value[status],
      count: profileCounts.value.parser_statuses?.[status] || 0,
    })
  )
);
const eventReplaySelectorPayload = computed(() =>
  Object.fromEntries(
    [
      ['parser_key', eventReplaySelectors.value.parserKey],
      ['provider_metric_name', eventReplaySelectors.value.providerMetricName],
      ['payload_shape_hash', eventReplaySelectors.value.payloadShapeHash],
    ].filter(([, value]) => Boolean(value))
  )
);
const hasEventReplaySelector = computed(
  () => Object.keys(eventReplaySelectorPayload.value).length > 0
);

/**
 * Formats provider timestamps for the current dashboard locale and timezone.
 *
 * @param {string|number|Date|null|undefined} timestamp Provider timestamp.
 * @returns {string} Human-readable localized timestamp.
 */
function formatProviderTimestamp(timestamp) {
  if (!timestamp) {
    return t('CONTACT_PANEL.NOT_AVAILABLE');
  }

  const parsedTimestamp = new Date(timestamp);
  if (Number.isNaN(parsedTimestamp.getTime())) {
    return t('CONTACT_PANEL.NOT_AVAILABLE');
  }

  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsedTimestamp);
}
const formattedSmokeTestTimestamp = computed(() =>
  formatProviderTimestamp(subscriptionSmokeTest.value?.tested_at)
);

/**
 * Normalizes backend settings into a complete reactive form object.
 *
 * @param {Partial<KlaviyoTwoWaySyncSettings>|undefined} settings
 * @returns {KlaviyoTwoWaySyncSettings}
 */
const normalizedTwoWaySettings = settings => {
  const defaultSettings = defaultTwoWaySyncSettings();
  const conflictStrategy = Object.fromEntries(
    Object.keys(defaultSettings.conflict_strategy).map(fieldGroup => [
      fieldGroup,
      settings?.conflict_strategy?.[fieldGroup] ||
        defaultSettings.conflict_strategy[fieldGroup],
    ])
  );
  return {
    enabled: settings?.enabled ?? defaultSettings.enabled,
    identity_sync_enabled:
      settings?.identity_sync_enabled ?? defaultSettings.identity_sync_enabled,
    dry_run: settings?.dry_run ?? defaultSettings.dry_run,
    conflict_strategy: conflictStrategy,
  };
};

/**
 * Returns the warning copy for automatic conflict strategies.
 *
 * @param {string} strategy
 * @returns {string}
 */
const conflictStrategyWarning = strategy => {
  if (strategy === 'klaviyo_wins') {
    return t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.WARNINGS.KLAVIYO_WINS');
  }
  if (strategy === 'glovely_wins') {
    return t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.WARNINGS.GLOVELY_WINS');
  }
  return '';
};

/**
 * Opens the integration behavior explainer.
 *
 * @returns {void}
 */
const openBehaviorDialog = () => {
  behaviorDialogRef.value?.open();
};

/**
 * Closes the integration behavior explainer.
 *
 * @returns {void}
 */
const closeBehaviorDialog = () => {
  behaviorDialogRef.value?.close();
};

/**
 * Opens the Klaviyo disconnect confirmation.
 *
 * @returns {void}
 */
const openDisconnectDialog = () => {
  disconnectDialogRef.value?.open();
};

/**
 * Closes the Klaviyo disconnect confirmation.
 *
 * @returns {void}
 */
const closeDisconnectDialog = () => {
  disconnectDialogRef.value?.close();
};

/**
 * Checks whether an API response means Klaviyo is unavailable for this account.
 *
 * @param {unknown} error API error raised by Axios.
 * @returns {boolean}
 */
const isKlaviyoDisabledResponse = error =>
  error?.response?.data?.code === 'klaviyo_disabled_for_account';

/**
 * Sends account admins back to the integrations list when Klaviyo is disabled.
 *
 * @param {unknown} error API error raised by Axios.
 * @returns {boolean} Whether the error was handled as a disabled integration.
 */
const redirectIfKlaviyoDisabled = error => {
  if (!isKlaviyoDisabledResponse(error)) {
    return false;
  }

  useAlert(
    error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.DISABLED_FOR_ACCOUNT')
  );
  router.replace({ name: 'settings_applications' });
  return true;
};

/**
 * Refreshes the Klaviyo status summary from the backend.
 *
 * @returns {Promise<void>}
 */
const fetchStatus = async () => {
  const klaviyoStatusResponse = await integrationAPI.getKlaviyoStatus();
  klaviyoStatus.value = klaviyoStatusResponse.data;
  twoWaySync.value = normalizedTwoWaySettings(
    klaviyoStatusResponse.data.two_way_sync
  );
};

/**
 * Loads integration metadata and the Klaviyo status payload.
 *
 * @returns {Promise<void>}
 */
const initialize = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('integrations/get');
    await fetchStatus();
  } catch (error) {
    if (!redirectIfKlaviyoDisabled(error)) {
      errorMessage.value =
        error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.KLAVIYO.STATUS_ERROR');
    }
  } finally {
    isLoading.value = false;
  }
};

/**
 * Persists the Klaviyo API key through the generic integration hook endpoint.
 *
 * @returns {Promise<void>}
 */
const saveConnection = async () => {
  errorMessage.value = '';
  if (!apiKey.value) {
    errorMessage.value = t('INTEGRATION_SETTINGS.KLAVIYO.API_KEY_REQUIRED');
    return;
  }

  isSaving.value = true;
  try {
    const settings = {
      api_key: apiKey.value,
      api_revision: apiRevision.value,
      sync_profiles: true,
    };
    const hookId = klaviyoHook.value?.id;
    if (hookId) {
      await store.dispatch('integrations/updateHook', {
        hookId,
        hook: {
          settings,
        },
      });
    } else {
      await store.dispatch('integrations/createHook', {
        hook: {
          app_id: 'klaviyo',
          settings,
        },
      });
    }
    apiKey.value = '';
    await fetchStatus();
    useAlert(t('INTEGRATION_SETTINGS.KLAVIYO.SAVE_SUCCESS'));
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value = t('INTEGRATION_SETTINGS.KLAVIYO.SAVE_ERROR');
  } finally {
    isSaving.value = false;
  }
};

/**
 * Persists account-level two-way sync policy settings.
 *
 * @returns {Promise<void>}
 */
const saveTwoWaySettings = async () => {
  isSavingTwoWaySettings.value = true;
  errorMessage.value = '';
  try {
    const klaviyoSettingsResponse = await integrationAPI.updateKlaviyoSettings({
      two_way_sync: twoWaySync.value,
    });
    klaviyoStatus.value = klaviyoSettingsResponse.data;
    twoWaySync.value = normalizedTwoWaySettings(
      klaviyoSettingsResponse.data.two_way_sync
    );
    useAlert(t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.SAVE_SUCCESS'));
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.SAVE_ERROR');
  } finally {
    isSavingTwoWaySettings.value = false;
  }
};

/**
 * Verifies the stored Klaviyo API key and updates connection state.
 *
 * @returns {Promise<void>}
 */
const testConnection = async () => {
  isTesting.value = true;
  errorMessage.value = '';
  try {
    const klaviyoTestResponse = await integrationAPI.testKlaviyo();
    klaviyoStatus.value = klaviyoTestResponse.data;
    useAlert(t('INTEGRATION_SETTINGS.KLAVIYO.TEST_SUCCESS'));
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.TEST_ERROR');
  } finally {
    isTesting.value = false;
  }
};

/**
 * Enqueues a full profile backfill for the configured Klaviyo integration.
 *
 * @returns {Promise<void>}
 */
const startBackfill = async () => {
  isBackfilling.value = true;
  errorMessage.value = '';
  try {
    const klaviyoBackfillResponse = await integrationAPI.backfillKlaviyo();
    klaviyoStatus.value = klaviyoBackfillResponse.data;
    useAlert(t('INTEGRATION_SETTINGS.KLAVIYO.BACKFILL_SUCCESS'));
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.BACKFILL_ERROR');
  } finally {
    isBackfilling.value = false;
  }
};

/**
 * Enqueues one manual Klaviyo Events API ingestion page.
 *
 * @returns {Promise<void>}
 */
const syncEvents = async () => {
  isSyncingEvents.value = true;
  errorMessage.value = '';
  try {
    const eventSyncResponse = await integrationAPI.syncKlaviyoEvents();
    klaviyoStatus.value = eventSyncResponse.data;
    useAlert(
      eventSyncResponse.data.message ||
        t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.SYNC_SUCCESS')
    );
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.SYNC_ERROR');
  } finally {
    isSyncingEvents.value = false;
  }
};

/**
 * Enqueues parser replay for raw Klaviyo events matching selector fields.
 *
 * @returns {Promise<void>}
 */
const replayEvents = async () => {
  if (!hasEventReplaySelector.value) {
    errorMessage.value = t(
      'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.REPLAY_SELECTOR_REQUIRED'
    );
    return;
  }

  isReplayingEvents.value = true;
  errorMessage.value = '';
  try {
    const eventReplayResponse = await integrationAPI.replayKlaviyoEvents(
      eventReplaySelectorPayload.value
    );
    klaviyoStatus.value = eventReplayResponse.data;
    useAlert(
      eventReplayResponse.data.message ||
        t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.REPLAY_SUCCESS')
    );
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.REPLAY_ERROR');
  } finally {
    isReplayingEvents.value = false;
  }
};

/**
 * Disconnects Klaviyo and refreshes the page into disconnected state.
 *
 * @returns {Promise<void>}
 */
const disconnectKlaviyo = async () => {
  isDisconnecting.value = true;
  errorMessage.value = '';
  try {
    const klaviyoDisconnectResponse = await integrationAPI.disconnectKlaviyo();
    klaviyoStatus.value = klaviyoDisconnectResponse.data;
    twoWaySync.value = normalizedTwoWaySettings(
      klaviyoDisconnectResponse.data.two_way_sync
    );
    apiKey.value = '';
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.SUCCESS'));
  } catch (error) {
    if (redirectIfKlaviyoDisabled(error)) {
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.ERROR');
  } finally {
    isDisconnecting.value = false;
  }
};

/**
 * Confirms Klaviyo disconnection after the destructive action dialog.
 *
 * @returns {Promise<void>}
 */
const confirmDisconnect = async () => {
  closeDisconnectDialog();
  await disconnectKlaviyo();
};

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="t('INTEGRATION_SETTINGS.KLAVIYO.HEADER')"
        description=""
        feature-name="crm_integration"
        :back-button-label="t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5 max-w-3xl">
        <section
          class="flex flex-col gap-4 p-4 outline outline-1 outline-n-container bg-n-card rounded-lg"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="text-heading-2 text-n-slate-12">
                {{
                  klaviyoIntegration.name ||
                  t('INTEGRATION_SETTINGS.KLAVIYO.HEADER')
                }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ klaviyoIntegration.description }}
              </p>
            </div>
            <span
              class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
            >
              {{ klaviyoStatus?.status || 'disabled' }}
            </span>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <Input
              v-model="apiKey"
              type="password"
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.API_KEY')"
              :placeholder="
                t('INTEGRATION_SETTINGS.KLAVIYO.API_KEY_PLACEHOLDER')
              "
            />
            <Input
              v-model="apiRevision"
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.API_REVISION')"
              placeholder="2026-04-15"
            />
          </div>

          <p v-if="errorMessage" class="text-sm text-n-ruby-9">
            {{ errorMessage }}
          </p>

          <section
            v-if="subscriptionSmokeTest"
            class="flex flex-col gap-3 p-3 border rounded-lg"
            :class="smokeTestPanelClasses"
          >
            <div class="flex items-start gap-2">
              <span
                class="mt-0.5 size-4 shrink-0"
                :class="
                  subscriptionSmokeTest.status === 'success'
                    ? 'i-lucide-check-circle-2'
                    : 'i-lucide-alert-triangle'
                "
              />
              <div>
                <p class="text-sm font-medium">
                  {{
                    subscriptionSmokeTest.status === 'success'
                      ? t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.READY')
                      : t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.WARNING')
                  }}
                </p>
                <p class="text-xs">
                  {{
                    t('INTEGRATION_SETTINGS.KLAVIYO.SMOKE_TEST.TESTED_AT', {
                      timestamp: formattedSmokeTestTimestamp,
                    })
                  }}
                </p>
              </div>
            </div>
            <dl class="grid gap-2 md:grid-cols-2">
              <div
                v-for="row in smokeTestRows"
                :key="row.channel"
                class="p-2 rounded-md bg-n-alpha-1"
              >
                <dt class="text-xs font-medium">
                  {{ row.label }}
                </dt>
                <dd class="mt-1 text-xs break-all">
                  {{ row.identifier || t('CONTACT_PANEL.NOT_AVAILABLE') }}
                </dd>
                <dd class="mt-1 text-sm">
                  {{ row.message }}
                </dd>
              </div>
            </dl>
          </section>

          <div class="flex flex-wrap gap-2">
            <Button
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.SAVE')"
              icon="i-lucide-save"
              :is-loading="isSaving"
              @click="saveConnection"
            />
            <Button
              faded
              :disabled="!isConnected"
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.TEST')"
              icon="i-lucide-plug-zap"
              :is-loading="isTesting"
              @click="testConnection"
            />
            <Button
              faded
              :disabled="!isConnected"
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.START_BACKFILL')"
              icon="i-lucide-refresh-cw"
              :is-loading="isBackfilling"
              @click="startBackfill"
            />
            <Button
              faded
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.OPEN')"
              icon="i-lucide-info"
              @click="openBehaviorDialog"
            />
            <Button
              faded
              ruby
              :disabled="!isConnected"
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.BUTTON')"
              icon="i-lucide-unplug"
              :is-loading="isDisconnecting"
              @click="openDisconnectDialog"
            />
          </div>
        </section>

        <section
          class="flex flex-col gap-4 p-4 outline outline-1 outline-n-container bg-n-card rounded-lg"
        >
          <div>
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.HEADER') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.DESCRIPTION') }}
            </p>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <label class="flex items-start gap-3 text-sm text-n-slate-12">
              <Checkbox v-model="twoWaySync.enabled" :disabled="!isConnected" />
              <span>
                <span class="block font-medium">
                  {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.ENABLED') }}
                </span>
                <span class="block text-n-slate-11">
                  {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.ENABLED_HELP') }}
                </span>
              </span>
            </label>

            <label class="flex items-start gap-3 text-sm text-n-slate-12">
              <Checkbox v-model="twoWaySync.dry_run" :disabled="!isConnected" />
              <span>
                <span class="block font-medium">
                  {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.DRY_RUN') }}
                </span>
                <span class="block text-n-slate-11">
                  {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.DRY_RUN_HELP') }}
                </span>
              </span>
            </label>

            <label class="flex items-start gap-3 text-sm text-n-slate-12">
              <Checkbox
                v-model="twoWaySync.identity_sync_enabled"
                :disabled="!isConnected"
              />
              <span>
                <span class="block font-medium">
                  {{ t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.IDENTITY_SYNC') }}
                </span>
                <span class="block text-n-slate-11">
                  {{
                    t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.IDENTITY_SYNC_HELP')
                  }}
                </span>
              </span>
            </label>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <div
              v-for="fieldControl in fieldStrategyControls"
              :key="fieldControl.key"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-12">
                {{ fieldControl.label }}
              </label>
              <Select
                v-model="twoWaySync.conflict_strategy[fieldControl.key]"
                :options="conflictStrategyOptions"
                :disabled="!isConnected"
              />
              <p
                v-if="
                  conflictStrategyWarning(
                    twoWaySync.conflict_strategy[fieldControl.key]
                  )
                "
                class="text-xs text-n-amber-11"
              >
                {{
                  conflictStrategyWarning(
                    twoWaySync.conflict_strategy[fieldControl.key]
                  )
                }}
              </p>
            </div>
          </div>

          <div>
            <Button
              :disabled="!isConnected"
              :label="t('INTEGRATION_SETTINGS.KLAVIYO.TWO_WAY.SAVE')"
              icon="i-lucide-sliders-horizontal"
              :is-loading="isSavingTwoWaySettings"
              @click="saveTwoWaySettings"
            />
          </div>
        </section>

        <section
          class="grid gap-3 p-4 outline outline-1 outline-n-container bg-n-card rounded-lg md:grid-cols-6"
        >
          <div>
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.PROFILES') }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ profileCounts.profiles || 0 }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.MATCHED') }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ profileCounts.matched || 0 }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.AMBIGUOUS') }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ profileCounts.ambiguous || 0 }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.UNMATCHED') }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ profileCounts.unmatched || 0 }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.FIELD_CONFLICTS') }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ profileCounts.field_conflicts || 0 }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.PENDING_OUTBOUND') }}
            </p>
            <p class="text-xl font-medium text-n-slate-12">
              {{ profileCounts.pending_outbound || 0 }}
            </p>
          </div>
        </section>

        <section
          class="flex flex-col gap-4 p-4 outline outline-1 outline-n-container bg-n-card rounded-lg"
        >
          <div>
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.HEADER') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.DESCRIPTION')
              }}
            </p>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <div>
              <p class="text-xs text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.RAW_EVENTS') }}
              </p>
              <p class="text-xl font-medium text-n-slate-12">
                {{ profileCounts.raw_events || 0 }}
              </p>
            </div>
            <div>
              <p class="text-xs text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.COUNTS.PARSED_EVENTS') }}
              </p>
              <p class="text-xl font-medium text-n-slate-12">
                {{ profileCounts.parsed_events || 0 }}
              </p>
            </div>
          </div>

          <dl class="grid gap-2 md:grid-cols-5">
            <div
              v-for="parserStatusRow in parserStatusRows"
              :key="parserStatusRow.status"
              class="p-3 rounded-md bg-n-alpha-1"
            >
              <dt class="text-xs text-n-slate-10">
                {{ parserStatusRow.label }}
              </dt>
              <dd class="mt-1 text-lg font-medium text-n-slate-12">
                {{ parserStatusRow.count }}
              </dd>
            </div>
          </dl>

          <div class="flex flex-wrap gap-2">
            <Button
              faded
              :disabled="!isConnected"
              :label="
                t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.SYNC_NOW')
              "
              icon="i-lucide-refresh-cw"
              :is-loading="isSyncingEvents"
              @click="syncEvents"
            />
          </div>

          <div class="grid gap-3 md:grid-cols-3">
            <Input
              v-model="eventReplaySelectors.parserKey"
              :label="
                t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PARSER_KEY')
              "
              placeholder="klaviyo.shopify_commerce"
              :disabled="!isConnected"
            />
            <Input
              v-model="eventReplaySelectors.providerMetricName"
              :label="
                t(
                  'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PROVIDER_METRIC_NAME'
                )
              "
              placeholder="Viewed Product"
              :disabled="!isConnected"
            />
            <Input
              v-model="eventReplaySelectors.payloadShapeHash"
              :label="
                t(
                  'INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.PAYLOAD_SHAPE_HASH'
                )
              "
              placeholder="sha256..."
              :disabled="!isConnected"
            />
          </div>

          <div>
            <Button
              faded
              :disabled="!isConnected || !hasEventReplaySelector"
              :label="
                t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_DIAGNOSTICS.REPLAY')
              "
              icon="i-lucide-repeat-2"
              :is-loading="isReplayingEvents"
              @click="replayEvents"
            />
          </div>
        </section>

        <section
          v-if="profileSyncCursor"
          class="p-4 outline outline-1 outline-n-container bg-n-card rounded-lg"
        >
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.KLAVIYO.SYNC_CURSOR') }}
          </h3>
          <dl class="grid gap-2 mt-3 text-sm md:grid-cols-3">
            <div>
              <dt class="text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.CURSOR_STATE') }}
              </dt>
              <dd class="text-n-slate-12">{{ profileSyncCursor.state }}</dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.LAST_SYNCED') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  profileSyncCursor.last_synced_at ||
                  t('CONTACT_PANEL.NOT_AVAILABLE')
                }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.HAS_MORE') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  profileSyncCursor.has_cursor
                    ? t('CHOICE_TOGGLE.YES')
                    : t('CHOICE_TOGGLE.NO')
                }}
              </dd>
            </div>
          </dl>
        </section>

        <section
          v-if="eventSyncCursor"
          class="p-4 outline outline-1 outline-n-container bg-n-card rounded-lg"
        >
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.KLAVIYO.EVENT_SYNC_CURSOR') }}
          </h3>
          <dl class="grid gap-2 mt-3 text-sm md:grid-cols-3">
            <div>
              <dt class="text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.CURSOR_STATE') }}
              </dt>
              <dd class="text-n-slate-12">{{ eventSyncCursor.state }}</dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.LAST_SYNCED') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  eventSyncCursor.last_synced_at ||
                  t('CONTACT_PANEL.NOT_AVAILABLE')
                }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('INTEGRATION_SETTINGS.KLAVIYO.HAS_MORE') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  eventSyncCursor.has_cursor
                    ? t('CHOICE_TOGGLE.YES')
                    : t('CHOICE_TOGGLE.NO')
                }}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <Dialog
        ref="behaviorDialogRef"
        width="3xl"
        position="top"
        :title="t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.TITLE')"
        :description="
          t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.DESCRIPTION')
        "
        :show-cancel-button="false"
        :confirm-button-label="
          t('INTEGRATION_SETTINGS.KLAVIYO.BEHAVIOR_MODAL.CLOSE')
        "
        overflow-y-auto
        @confirm="closeBehaviorDialog"
      >
        <div class="grid gap-3 md:grid-cols-2">
          <section
            v-for="section in behaviorSections"
            :key="section.key"
            class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-2 outline outline-1 outline-n-container"
          >
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ section.title }}
            </h4>
            <ul
              class="flex flex-col gap-1 ps-4 text-sm list-disc text-n-slate-11"
            >
              <li v-for="bullet in section.bullets" :key="bullet">
                {{ bullet }}
              </li>
            </ul>
          </section>
        </div>
      </Dialog>
      <Dialog
        ref="disconnectDialogRef"
        type="alert"
        :title="t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.TITLE')"
        :description="t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.MESSAGE')"
        :confirm-button-label="
          t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.CONFIRM')
        "
        :cancel-button-label="
          t('INTEGRATION_SETTINGS.KLAVIYO.DISCONNECT.CANCEL')
        "
        @confirm="confirmDisconnect"
      />
    </template>
  </SettingsLayout>
</template>
